'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Paper, 
  Title, 
  Table, 
  Button, 
  Text, 
  Group, 
  Badge, 
  Loader,
  ActionIcon,
  Menu
} from '@mantine/core';
import { IconDotsVertical, IconRefresh, IconSend, IconTrash } from '@tabler/icons-react';
import { getInvitationsByOrg, resendInvitation, cancelInvitation } from '../services/firestore';
import { Invitation, InvitationListProps } from '../types';

export const InvitationList: React.FC<InvitationListProps> = ({ 
  orgId,
  onResendSuccess,
  onCancelSuccess,
  onError,
  onInvitationResent
}) => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  
  const loadInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const orgInvitations = await getInvitationsByOrg(orgId);
      setInvitations(orgInvitations);
    } catch (error) {
      console.error('Error loading invitations:', error);
      onError?.(error as Error);
    } finally {
      setLoading(false);
    }
  }, [orgId, onError]);
  
  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);
  
  const handleResend = async (invitationId: string) => {
    try {
      const updatedInvitation = await resendInvitation(invitationId, {
        onInvitationResent: (invitation) => {
          // This will be called after the invitation is resent
          onInvitationResent?.(invitation);
        }
      });
      
      onResendSuccess?.(
        updatedInvitation.token, 
        updatedInvitation.email, 
        updatedInvitation.orgName, 
        updatedInvitation.inviterName || ''
      );
      
      // Refresh the list
      loadInvitations();
    } catch (error) {
      console.error('Error resending invitation:', error);
      onError?.(error as Error);
    }
  };
  
  const handleCancel = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
      
      // Update local state
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
      onCancelSuccess?.();
    } catch (error) {
      console.error('Error canceling invitation:', error);
      onError?.(error as Error);
    }
  };
  
  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString();
  };
  
  if (loading) {
    return <Loader />;
  }
  
  return (
    <Paper radius="md" p="xl" withBorder>
      <Group justify="apart" mb="md">
        <Title order={2}>Pending Invitations</Title>
        <Button 
          leftSection={<IconRefresh size={16} />} 
          variant="subtle"
          onClick={loadInvitations}
        >
          Refresh
        </Button>
      </Group>
      
      {invitations.length === 0 ? (
        <Text color="dimmed">No pending invitations found.</Text>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Invited By</th>
              <th>Sent Date</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((invitation) => (
              <tr key={invitation.id}>
                <td>{invitation.email}</td>
                <td style={{ textTransform: 'capitalize' }}>{invitation.role}</td>
                <td>{invitation.inviterName}</td>
                <td>{formatDate(invitation.createdAt)}</td>
                <td>{formatDate(invitation.expiresAt)}</td>
                <td>
                  <Badge 
                    color={
                      invitation.status === 'pending' ? 'blue' : 
                      invitation.status === 'accepted' ? 'green' : 'red'
                    }
                  >
                    {invitation.status}
                  </Badge>
                </td>
                <td>
                  {invitation.status === 'pending' && (
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon>
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      
                      <Menu.Dropdown>
                        <Menu.Item 
                          leftSection={<IconSend size={16} />}
                          onClick={() => handleResend(invitation.id)}
                        >
                          Resend Invitation
                        </Menu.Item>
                        
                        <Menu.Item 
                          leftSection={<IconTrash size={16} />}
                          onClick={() => handleCancel(invitation.id)}
                          color="red"
                        >
                          Cancel Invitation
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Paper>
  );
}; 